import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormErrorMessage from '~/components/Form/next/FormErrorMessage';
import { Button, StyledInput } from '~/components/ui';

const TOTAL_MINUTES = 24 * 60;
const SNAP_INTERVAL = 15;
const MIN_DURATION = 30;
const DEFAULT_DURATION = 60;

type TimeSlot = { start?: string; end?: string };

type DragState = {
    type: 'move' | 'resize' | 'create';
    edge?: 'start' | 'end';
    index: number;
    startMinutes: number;
    initialStart: number;
    initialEnd: number;
} | null;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const minutesToTime = (minutes: number) => {
    const safeMinutes = clamp(Number.isFinite(minutes) ? minutes : 0, 0, TOTAL_MINUTES);
    if (safeMinutes === TOTAL_MINUTES) return '24:00';
    const hour = Math.floor(safeMinutes / 60);
    const minute = safeMinutes % 60;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

const timeToMinutes = (value?: string | null) => {
    if (!value) return null;
    const match = value.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    return Number(match[1]) * 60 + Number(match[2]);
};

export interface TimeRangesInputProps {
    name: string;
    label?: string;
}

export default function TimeRangesInput({ name, label }: TimeRangesInputProps) {
    const { t } = useTranslation('form');
    const resolvedLabel = label ?? t('timeRangesInput.defaultLabel');
    const containerRef = useRef<HTMLDivElement>(null);
    const [dragState, setDragState] = useState<DragState>(null);
    const { control, setValue, getFieldState } = useFormContext();
    const { fields, append, remove, replace } = useFieldArray({ control, name });

    const watchedSlots = (useWatch({ control, name }) as TimeSlot[] | undefined) ?? [];
    const hasError = Boolean(getFieldState(name).error);

    const mergeSlots = (slots: TimeSlot[]) => {
        const sorted = slots
            .map((s) => ({ start: timeToMinutes(s.start) ?? 0, end: timeToMinutes(s.end) ?? 0 }))
            .filter((s) => s.end > s.start)
            .sort((a, b) => a.start - b.start);

        if (sorted.length === 0) return [];

        const merged = [sorted[0]];
        for (let i = 1; i < sorted.length; i++) {
            const last = merged[merged.length - 1];
            const current = sorted[i];
            if (current.start <= last.end) {
                last.end = Math.max(last.end, current.end);
            } else {
                merged.push(current);
            }
        }
        return merged.map((s) => ({ start: minutesToTime(s.start), end: minutesToTime(s.end) }));
    };

    const getMinutesFromEvent = (event: MouseEvent | TouchEvent | ReactMouseEvent | ReactTouchEvent) => {
        if (!containerRef.current) return 0;
        const rect = containerRef.current.getBoundingClientRect();
        const clientX = 'touches' in event ? event.touches[0]?.clientX : (event as MouseEvent).clientX;
        const percentage = clamp((clientX - rect.left) / rect.width, 0, 1);
        return Math.round((percentage * TOTAL_MINUTES) / SNAP_INTERVAL) * SNAP_INTERVAL;
    };

    const handleCreate = (event: ReactMouseEvent | ReactTouchEvent) => {
        if (event.target !== containerRef.current) return;
        const startMins = getMinutesFromEvent(event);
        const endMins = clamp(startMins + DEFAULT_DURATION, 0, TOTAL_MINUTES);

        append({ start: minutesToTime(startMins), end: minutesToTime(endMins) });
        setDragState({
            type: 'create',
            index: fields.length,
            startMinutes: startMins,
            initialStart: startMins,
            initialEnd: endMins,
            edge: 'end'
        });
    };

    const startInteraction = (
        e: ReactMouseEvent | ReactTouchEvent,
        index: number,
        type: 'move' | 'resize',
        edge?: 'start' | 'end'
    ) => {
        e.stopPropagation();
        const start = timeToMinutes(watchedSlots[index].start) ?? 0;
        const end = timeToMinutes(watchedSlots[index].end) ?? 0;
        setDragState({
            type,
            index,
            edge,
            startMinutes: getMinutesFromEvent(e),
            initialStart: start,
            initialEnd: end
        });
    };

    useEffect(() => {
        if (!dragState) return;

        const handleMove = (e: MouseEvent | TouchEvent) => {
            const currentMins = getMinutesFromEvent(e);
            const diff = currentMins - dragState.startMinutes;
            let newStart = dragState.initialStart;
            let newEnd = dragState.initialEnd;

            if (dragState.type === 'move') {
                const duration = dragState.initialEnd - dragState.initialStart;
                newStart = clamp(dragState.initialStart + diff, 0, TOTAL_MINUTES - duration);
                newEnd = newStart + duration;
            } else if (dragState.edge === 'start') {
                newStart = clamp(dragState.initialStart + diff, 0, dragState.initialEnd - MIN_DURATION);
            } else {
                newEnd = clamp(dragState.initialEnd + diff, dragState.initialStart + MIN_DURATION, TOTAL_MINUTES);
            }

            setValue(`${name}.${dragState.index}.start`, minutesToTime(newStart));
            setValue(`${name}.${dragState.index}.end`, minutesToTime(newEnd));
        };

        const handleUp = () => {
            const currentValues = (control._formValues[name] as TimeSlot[]) || [];
            replace(mergeSlots(currentValues));
            setDragState(null);
        };

        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        window.addEventListener('touchmove', handleMove);
        window.addEventListener('touchend', handleUp);
        return () => {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', handleUp);
        };
    }, [dragState, name, setValue, replace, control]);

    return (
        <div className="tw-my-4">
            {resolvedLabel && (
                <label className="tw-inline-block tw-pr-2 tw-text-[14px] tw-text-accents-9">{resolvedLabel}</label>
            )}

            <div className="tw-mb-0.5 tw-flex tw-justify-between tw-px-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-text-accents-5">
                {['00:00', '06:00', '12:00', '18:00', '24:00'].map((l) => (
                    <span key={l}>{l}</span>
                ))}
            </div>

            <div
                ref={containerRef}
                onMouseDown={handleCreate}
                className={`tw-relative tw-h-12 tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-bg-accents-0 ${hasError ? 'tw-border-error' : 'tw-border-accents-3'}`}
            >
                <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-grid tw-grid-cols-8">
                    {Array.from({ length: 8 }).map((_, index) => (
                        <div
                            key={index}
                            className="tw-border-0 tw-border-r tw-border-solid tw-border-accents-3/70 last:tw-border-r-0"
                        />
                    ))}
                </div>

                {fields.map((field, index) => {
                    const start = timeToMinutes(watchedSlots[index]?.start) ?? 0;
                    const end = timeToMinutes(watchedSlots[index]?.end) ?? 0;
                    const left = (start / TOTAL_MINUTES) * 100;
                    const width = ((end - start) / TOTAL_MINUTES) * 100;
                    const atLeftEdge = start === 0;
                    const atRightEdge = end === TOTAL_MINUTES;
                    const edgeOffset = (atLeftEdge ? 4 : 0) + (atRightEdge ? 4 : 0);
                    return (
                        <div
                            key={field.id}
                            className="tw-absolute tw-inset-y-1 tw-z-10"
                            style={{
                                left: atLeftEdge ? '4px' : `${left}%`,
                                width: edgeOffset > 0 ? `calc(${width}% - ${edgeOffset}px)` : `${width}%`,
                                minWidth: '44px'
                            }}
                        >
                            <div
                                onMouseDown={(e) => startInteraction(e, index, 'move')}
                                className="tw-group tw-relative tw-flex tw-h-full tw-cursor-move tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-success tw-bg-success/80 tw-text-white tw-transition-colors hover:tw-bg-success/90"
                            >
                                <div className="tw-pointer-events-none tw-w-full tw-px-4 tw-text-center">
                                    <div className="tw-truncate tw-text-[11px] tw-font-semibold">
                                        {watchedSlots[index]?.start} - {watchedSlots[index]?.end}
                                    </div>
                                </div>

                                <button
                                    onMouseDown={(e) => startInteraction(e, index, 'resize', 'start')}
                                    className="tw-absolute tw-left-0 tw-h-full tw-w-3 !tw-cursor-ew-resize tw-border-0 tw-bg-transparent"
                                />
                                <span className="tw-pointer-events-none tw-absolute tw-left-1.5 tw-top-1/2 tw-h-5 tw-w-px -tw-translate-y-1/2 tw-bg-accents-3" />
                                <button
                                    onMouseDown={(e) => startInteraction(e, index, 'resize', 'end')}
                                    className="tw-absolute tw-right-0 tw-h-full tw-w-3 !tw-cursor-ew-resize tw-border-0 tw-bg-transparent"
                                />
                                <span className="tw-pointer-events-none tw-absolute tw-right-1.5 tw-top-1/2 tw-h-5 tw-w-px -tw-translate-y-1/2 tw-bg-accents-3" />
                            </div>
                        </div>
                    );
                })}
            </div>
            <span className="tw-mt-0.5 tw-block tw-text-center tw-text-xs tw-italic tw-text-accents-5">
                {t('timeRangesInput.clickToCreateHint')}
            </span>

            <div className="tw-mt-2 tw-space-y-2">
                {fields.map((field, index) => (
                    <div key={field.id} className="tw-grid tw-grid-cols-[1fr_1fr_auto] tw-gap-2">
                        <StyledInput
                            name={`${name}.${index}.start`}
                            labelLeft={t('timeRangesInput.startLabel')}
                            type="time"
                            value={watchedSlots[index]?.start ?? ''}
                            onChange={(event) =>
                                setValue(`${name}.${index}.start`, event.target.value, {
                                    shouldValidate: true,
                                    shouldDirty: true
                                })
                            }
                            flush
                            fullWidth
                        />
                        <StyledInput
                            name={`${name}.${index}.end`}
                            labelLeft={t('timeRangesInput.endLabel')}
                            type="time"
                            value={watchedSlots[index]?.end ?? ''}
                            onChange={(event) =>
                                setValue(`${name}.${index}.end`, event.target.value, {
                                    shouldValidate: true,
                                    shouldDirty: true
                                })
                            }
                            flush
                            fullWidth
                        />
                        <Button type="button" size="sm" flat color="error" onPress={() => remove(index)}>
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </div>
                ))}
            </div>

            <div className="tw-mt-2">
                <FormErrorMessage name={name} />
            </div>
        </div>
    );
}
